import { isOdd, sum } from "../../datamanipulation/index.js";
function assert(value) {
    if (!value) {
        throw new Error();
    }
}
/**
 * Budto mame neroztridena (raw) data:
 *   DISKRETNI
 *   - bud predstavuji celou populaci -> mame presne rozdeleni:          (smerodatna odchylka rozdeleni je std)
 *   - vyber - jaky? s vracenim / bez vraceni  ? (!!!!!)                 (odhad smerodatne odchylky rozdeleni je stdSample ; odhady F a q jsou slozitejsi)      PROZKOUMAT
 *                                ^ bez vraceni by to nebyly nezavisle veliciny - nahodny vyber je i.i.d. (independent identically distributed)
 *   SPOJITE:
 *   - vyber, 's vracenim' (at uz ze spojiteho ci diskretniho rozdeleni)
 *
 *
 * Nebo mame diskretni rozdeleni samotne (body a pravdepodobnosti /vahy/)
 *   ~ neni treba delat odhady, statistiky lze pocitat primo (napr smerodatna odchylka je std, ne stdSample), známe p, F, q
 *
 *
 * Nebo mame roztridena data:
 *   DISKRETNI ROZDELENI:
 *   // v podstate nema cenu tridit - a pokud s tridi, tak je to
 *   //   * bud bezztratove (tridy = hodnoty ; nebo do kazde tridy padne max 1 disk. hodnota)
 *   //   * nebo ztratove (prilis siroke tridy - mix)
 *   - cela populace - beze ztraty: -> mame presne rozdeleni
 *   - cela populace se ztratou: k cemu by to bylo?
 *   - vyber - beze ztraty:  -> gen. odhad pro prumer a rozptyl; specificke odhady pro nektere statistiky (?) - na zaklade predpokladu o/znalosti rozdeleni (pr. binomicke)
 *   - vyber - se ztratou: k cemu by to bylo?
 *
 *   SPOJITE ROZDELENI:
 *   - vyber - se ztratou:    - slozitejsi odhady u F, q
 *
 *
 *   trideni u odhadu prumeru nevede ke ztrate informace? ale jo, vede - posun hodnot na stredy trid muze byt biased (vsechny hodnoty se zvetsi / vsechny hodnoty se zmensi)
 *
 *  ... spojity vyber je vzdy s vracenim - opakujeme stejny nezavisly pokus
 *  ... diskretni vyber muze byt s vracenim nebo bez ; pokud je bez tak je to non-standard! (neni i.i.d.)
 */
export class Stats {
    constructor(sample, weights, originalSampleSize) {
        this.size = 0;
        this.min = NaN;
        this.max = NaN;
        this.range = NaN;
        this.average = NaN;
        this.median = NaN;
        this.lowerQuartile = NaN;
        this.upperQuartile = NaN;
        this.iqr = NaN; // inter-quartile range
        this.std = NaN;
        this.sampleStd = NaN;
        this.logUnit = 0;
        this.F = [];
        // HANDLE WEIGHTS
        this.isWeighted = weights !== undefined;
        // no weights = this is a raw sample, every datapoint has weight 1/n ; originalSampleSize = n (ovewrite if supplied)
        if (weights === undefined) {
            const n = sample.length;
            weights = Array(n).fill(1 / n); // numeric errors?
            originalSampleSize = n;
        }
        else {
            assert(sample.length == weights.length);
            if (sample.length > 0) {
                const total = sum(weights);
                if (Math.round(total) == 1) {
                    // weights are relative frequencies,
                    // originalSampleSize cannot be inferred - it must be supplied (for the calculation of sample std) - does this estimation make even sense here? Ask M.F.
                    assert(originalSampleSize !== undefined);
                    // if it's undefined, we may assume that this a def of discrete distribution (exact), then we could use originalSampleSize = sample.length
                    assert(Math.abs(total - 1) < 1e-8); // eps
                }
                else {
                    // weights are absolute frequencies - original sample size is their sum (supplied value is ignored)
                    for (const w of weights)
                        assert(Math.round(w) === w);
                    weights = weights.map(w => w / total);
                    originalSampleSize = total;
                }
            }
            else {
                // supplied sample has length 0
                originalSampleSize = 0;
                weights = [];
            }
        }
        this.weights = weights;
        this.originalSampleSize = Number(originalSampleSize);
        // -------------
        // order matters!
        this.orderedSample = sample.sort((a, b) => a - b); // fuck!
        this.size = sample.length;
        this.min = this._min();
        this.max = this._max();
        this.range = this._range();
        this.average = this._average();
        this.std = this._std();
        this.sampleStd = this._sampleStd();
        this.F = this._F();
        this.median = this._median();
        this.lowerQuartile = this._quantile(0.25);
        this.upperQuartile = this._quantile(0.75);
        this.iqr = this.upperQuartile - this.lowerQuartile;
        this.logUnit = Math.ceil(Math.log10(this.range));
    }
    _min() {
        if (!this.size)
            return NaN;
        return this.orderedSample[0];
    }
    _max() {
        if (!this.size)
            return NaN;
        return this.orderedSample[this.size - 1];
    }
    // range = max - min   variacni rozpeti R    https://en.wikipedia.org/wiki/Range_(statistics)
    _range() {
        return this.max - this.min;
    }
    _median() {
        if (!this.size)
            return NaN;
        // TODO: if weighted based on bins (roztrideny soubor), another approximation should be used!
        // on the other hand if this is a genuine distribution (e.g. binomial), then this is correct
        if (this.isWeighted) {
            return this._quantile(0.5);
        }
        if (isOdd(this.size)) {
            return this.orderedSample[(this.size - 1) / 2];
        }
        else {
            const i = this.size / 2;
            return (this.orderedSample[i] + this.orderedSample[i - 1]) / 2;
        }
    }
    _average() {
        if (!this.size)
            return NaN;
        if (this.isWeighted) {
            // works for non-weighted as well, but may be less numerically accurate, and is slower
            return sum(this.orderedSample.map((x, i) => x * this.weights[i]));
        }
        else {
            return sum(this.orderedSample) / this.size;
        }
    }
    _std() {
        if (!this.size)
            return NaN;
        if (this.isWeighted) {
            const avg = this.average;
            return Math.sqrt(sum(this.orderedSample.map((x, i) => this.weights[i] * Math.pow((x - avg), 2))));
        }
        else {
            const avg = this.average;
            return Math.sqrt(sum(this.orderedSample.map(x => Math.pow((x - avg), 2))) / this.size);
        }
    }
    /**
     *  sample standard deviation ; vyberova smerodatna odchylka ; s nebo sigma ^
     */
    _sampleStd() {
        if (this.originalSampleSize <= 1)
            return NaN;
        return this.std * this.originalSampleSize / (this.originalSampleSize - 1);
    }
    _F() {
        // TODO: if weighted based on bins (roztrideny soubor), another approximation should be used (?)
        // on the other hand if this is a genuine distribution (e.g. binomial), then this is correct
        const p = this.weights;
        let prevRecord = {
            x: NaN,
            Fx: 0,
        };
        const F = [];
        this.orderedSample.forEach((value, i, number) => {
            if (value === prevRecord.x) {
                prevRecord.Fx += p[i];
            }
            else {
                let record = {
                    x: value,
                    Fx: prevRecord.Fx + p[i]
                };
                F.push(record);
                prevRecord = record;
            }
        });
        console.log(F);
        return F;
    }
    _quantile(q) {
        // TODO: if weighted based on bins (roztrideny soubor), another approximation should be used (?)
        // on the other hand if this is a genuine distribution (e.g. binomial), then this is correct
        //
        // Technical - if F was calculated differently, then perhaps this is ok
        // /compare in wikipedia - percentile/quantile - approximations to the quantile function (C param, matlab, etc.)/
        //
        // https://en.wikipedia.org/wiki/Quantile#Estimating_quantiles_from_a_sample
        //
        for (const r of this.F) {
            if (r.Fx >= q) {
                return r.x;
            }
        }
        return NaN;
    }
}
//# sourceMappingURL=stats.js.map